// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * ui.ts — the screens that are not the game itself.
 *
 * The results screen carries principle #9: it shows EVERY player's breakdown,
 * not just yours, and in solo it shows what you left on the table by running a
 * bot over your exact seed. The one moment players compare themselves is worth
 * more than a number reflected back at you.
 */

import { hashSeed } from '@ben-gy/game-engine/rng';
import { Game } from './game';
import { BOT_PRESETS, botPlay } from './bot';
import { MODES, modeOf, type Mode } from './modes';
import { button, el, escapeHtml, fmt } from './dom';
import type { PlayerResult } from './runner';

export interface Benchmark {
  score: number;
  lines: number;
  pieces: number;
  trimPct: number;
}

/** A deterministic UTC-day seed, so everyone gets the same well. */
export function dailySeed(now = new Date()): number {
  const key = `${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()}`;
  return hashSeed(`ballast-daily-${key}`);
}

export function dailyLabel(now = new Date()): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', day: 'numeric', month: 'short',
  }).format(now);
}

export function shareLink(mode: Mode, seed: number): string {
  const u = new URL(location.href);
  u.search = '';
  u.searchParams.set('mode', mode.id);
  u.searchParams.set('seed', String(seed >>> 0));
  return u.toString();
}

/**
 * What a solid run on THIS well looks like — computed by playing the seed out
 * with the heuristic bot. It is the "here is what you missed" half of the
 * results screen, and it is honest because it is the same simulation you played.
 */
export function benchmarkFor(mode: Mode, seed: number): Benchmark {
  const g = new Game({ mode, seed });
  const w = BOT_PRESETS.sailor;
  for (let i = 0; i < 400; i++) if (!botPlay(g, w) || g.over) break;
  return {
    score: g.score,
    lines: g.lines,
    pieces: g.pieces,
    trimPct: g.pieces > 0 ? Math.round((g.trimmed / g.pieces) * 100) : 0,
  };
}

// ------------------------------------------------------------------- menu ---

export interface MenuOpts {
  container: HTMLElement;
  mode: Mode;
  best: number;
  muted: boolean;
  onMode: (m: Mode) => void;
  onSolo: () => void;
  onRace: () => void;
  onDaily: () => void;
  onFriends: () => void;
  onHowTo: () => void;
  onAbout: () => void;
  onMute: (m: boolean) => void;
}

export function renderMenu(o: MenuOpts): void {
  const c = o.container;
  c.innerHTML = '';
  const wrap = el('div', 'menu');

  const title = el('div', 'title');
  title.innerHTML = `<h1>Ballast</h1><p class="tagline">The well hangs from a pivot. Where you stack decides how it hangs — and how it hangs decides where the next piece can go.</p>`;

  const modeRow = el('div', 'modes');
  const modeLabel = el('p', 'modes-k');
  modeLabel.textContent = 'Choose a well';
  const modeBtns = el('div', 'mode-btns');
  modeBtns.setAttribute('role', 'radiogroup');
  modeBtns.setAttribute('aria-label', 'Well');
  for (const m of MODES) {
    const b = button('', 'mode-btn');
    b.innerHTML = `<span class="mb-name">${escapeHtml(m.name)}</span><span class="mb-blurb">${escapeHtml(m.blurb)}</span>`;
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', String(m.id === o.mode.id));
    b.classList.toggle('on', m.id === o.mode.id);
    b.addEventListener('click', () => o.onMode(m));
    modeBtns.appendChild(b);
  }
  modeRow.append(modeLabel, modeBtns);

  const actions = el('div', 'actions');
  const play = button('Play', 'btn btn-primary');
  play.addEventListener('click', o.onSolo);
  const race = button('Race the crew', 'btn');
  race.addEventListener('click', o.onRace);
  const daily = button(`Daily well · ${dailyLabel()}`, 'btn');
  daily.addEventListener('click', o.onDaily);
  const friends = button('Play with friends', 'btn');
  friends.addEventListener('click', o.onFriends);
  actions.append(play, race, daily, friends);

  const meta = el('div', 'menu-meta');
  const best = el('p', 'best');
  best.textContent = o.best > 0 ? `Your best on ${o.mode.name}: ${fmt(o.best)}` : 'No run on this well yet.';
  const small = el('div', 'small-actions');
  const how = button('How to play', 'link-btn');
  how.addEventListener('click', o.onHowTo);
  const about = button('About', 'link-btn');
  about.addEventListener('click', o.onAbout);
  const mute = button(o.muted ? 'Sound off' : 'Sound on', 'link-btn');
  mute.addEventListener('click', () => { o.onMute(!o.muted); });
  small.append(how, about, mute);
  meta.append(best, small);

  wrap.append(title, modeRow, actions, meta);
  c.appendChild(wrap);
}

// ------------------------------------------------------------------ modal ---

export function openModal(title: string, bodyHtml: string): HTMLElement {
  const back = el('div', 'modal-back');
  const card = el('div', 'modal');
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-label', title);
  card.innerHTML = `<h2>${escapeHtml(title)}</h2><div class="modal-body">${bodyHtml}</div>`;
  const close = button('Close', 'btn');
  close.addEventListener('click', () => back.remove());
  card.appendChild(close);
  back.appendChild(card);
  back.addEventListener('click', (e) => { if (e.target === back) back.remove(); });
  document.body.appendChild(back);
  close.focus();
  return back;
}

export const HOW_TO_HTML = `
  <p>Stack the falling pieces and clear full rows.</p>
  <p><strong>The well hangs from a pivot.</strong> Pile up on one side and it lists — pieces
  then slide <em>downhill</em> as they fall, and the far side goes out of reach. The ghost
  always shows where a piece will really land, drift included.</p>
  <p>Clear a row while the well is <strong>IN TRIM</strong> and it scores double.</p>
  <p>Watch for the <strong>ingot</strong>: a single heavy cell. It fills any hole and wrecks your trim.</p>
  <ul class="keys">
    <li><b>Touch</b> — drag to move · tap to rotate · swipe down to drop · swipe up to hold · press and hold to soft drop</li>
    <li><b>Keyboard</b> — ◂ ▸ move · ▾ soft drop · Space hard drop · ▴/X rotate · Z rotate back · C hold · P pause</li>
  </ul>`;

export const ABOUT_HTML = `
  <p><strong>Ballast</strong> is a falling-block game with one rule added: the well hangs from a
  pivot, so the shape of your stack decides which columns you can still reach.</p>
  <p>It runs entirely in your browser. No accounts, no cookies, no tracking beyond an anonymous,
  cookie-less page-view count (Cloudflare Web Analytics). Your scores stay on your device.</p>
  <p><strong>Playing with friends</strong> connects your browsers <em>directly</em> to each other
  (peer-to-peer WebRTC). A public signalling relay is used only to introduce the two browsers;
  no game state ever touches a server, because there is no server. Connecting does share your IP
  address with the people in your room, the same as any direct connection.</p>
  <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.</p>`;

// ---------------------------------------------------------------- results ---

export interface ResultsOpts {
  container: HTMLElement;
  results: PlayerResult[];
  mode: Mode;
  seed: number;
  multiplayer: boolean;
  benchmark: Benchmark | null;
  best: number;
  tally: Record<string, number>;
  waitingText?: string;
  onAgain: () => void;
  onLobby?: () => void;
  onMenu: () => void;
  onShare: () => void;
  onFeedback: () => void;
}

export function renderResults(o: ResultsOpts): HTMLElement {
  const c = o.container;
  c.innerHTML = '';
  const wrap = el('div', 'results');

  const ranked = [...o.results].sort((a, b) => b.score - a.score);
  const me = ranked.find((r) => r.isSelf);
  const myRank = me ? ranked.indexOf(me) + 1 : 0;

  const head = el('div', 'res-head');
  const heading = o.multiplayer || ranked.length > 1
    ? `${myRank === 1 ? 'You took it' : `${myRank}${ordinal(myRank)} of ${ranked.length}`}`
    : 'Run over';
  head.innerHTML = `<h2>${escapeHtml(heading)}</h2>
    <p class="res-sub">${escapeHtml(o.mode.name)} · well ${o.seed >>> 0}</p>`;
  wrap.appendChild(head);

  if (me && me.score >= o.best && me.score > 0) {
    const pb = el('p', 'res-pb');
    pb.textContent = 'New personal best on this well.';
    wrap.appendChild(pb);
  }

  // Everyone's breakdown — the whole point of this screen.
  const table = el('div', 'res-table');
  const header = el('div', 'res-row res-hdr');
  header.innerHTML = `<span>Player</span><span>Score</span><span>Rows</span><span>In trim</span><span>Ballast</span>`;
  table.appendChild(header);
  for (const r of ranked) {
    const row = el('div', `res-row${r.isSelf ? ' is-self' : ''}`);
    const trimPct = r.pieces > 0 ? Math.round((r.trimmed / r.pieces) * 100) : 0;
    const tag = r.left ? ' <span class="res-tag">left</span>'
      : r.isBot ? ' <span class="res-tag">crew</span>' : '';
    row.innerHTML = `
      <span class="res-name">${escapeHtml(r.name)}${tag}</span>
      <span>${fmt(r.score)}</span>
      <span>${r.lines}${r.quads > 0 ? ` <em>·${r.quads}Q</em>` : ''}</span>
      <span>${trimPct}%</span>
      <span>${r.ballastSent}▲ ${r.ballastReceived}▼</span>`;
    table.appendChild(row);
  }
  wrap.appendChild(table);

  if (Object.keys(o.tally).length > 0) {
    const t = el('p', 'res-tally');
    t.textContent = `Match: ${ranked.map((r) => `${r.name} ${o.tally[r.id] ?? 0}`).join(' · ')}`;
    wrap.appendChild(t);
  }

  // What you missed — a bot's line on your exact well.
  if (o.benchmark && me) {
    const b = o.benchmark;
    const bm = el('div', 'res-bench');
    const delta = me.score - b.score;
    bm.innerHTML = `<h3>The harbourmaster's line on this well</h3>
      <p>${fmt(b.score)} points · ${b.lines} rows · ${b.pieces} pieces · ${b.trimPct}% in trim.
      You were ${delta >= 0 ? `<strong>${fmt(delta)} ahead</strong>` : `${fmt(-delta)} behind`}.</p>`;
    wrap.appendChild(bm);
  }

  if (o.waitingText) {
    const w = el('p', 'res-waiting');
    w.innerHTML = `<span class="spinner" aria-hidden="true"></span> ${escapeHtml(o.waitingText)}`;
    wrap.appendChild(w);
  }

  const actions = el('div', 'actions');
  const again = button(o.multiplayer ? 'Play again' : 'Run it back', 'btn btn-primary');
  again.addEventListener('click', o.onAgain);
  actions.appendChild(again);
  if (o.onLobby) {
    const lob = button('Back to lobby', 'btn');
    lob.addEventListener('click', o.onLobby);
    actions.appendChild(lob);
  }
  const share = button('Share this well', 'btn');
  share.addEventListener('click', o.onShare);
  const menu = button('Menu', 'btn');
  menu.addEventListener('click', o.onMenu);
  actions.append(share, menu);
  wrap.appendChild(actions);

  const fb = button('Something wrong? Tell me', 'link-btn results-feedback');
  fb.addEventListener('click', o.onFeedback);
  wrap.appendChild(fb);

  c.appendChild(wrap);
  return wrap;
}

function ordinal(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
}

/** Read ?mode= / ?seed= from a share link. Unknown ids fall back, never crash. */
export function readShareParams(): { mode: Mode | null; seed: number | null } {
  const p = new URL(location.href).searchParams;
  const rawMode = p.get('mode');
  const rawSeed = p.get('seed');
  const seed = rawSeed !== null && /^\d+$/.test(rawSeed) ? Number(rawSeed) >>> 0 : null;
  return { mode: rawMode ? modeOf(rawMode) : null, seed };
}

export function clearShareParams(): void {
  const u = new URL(location.href);
  u.searchParams.delete('mode');
  u.searchParams.delete('seed');
  history.replaceState(null, '', u.toString());
}
